import cn from "classnames";
import { graphql, Link, PageProps } from "gatsby";
import parse, {
  domToReact,
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";
import React from "react";
import { MdLink } from "react-icons/md";
import { DocNode } from "../../gatsby-node-transformer-asciidoc.types";
import CopyToClipboard from "../components/copyToClipboard";
import ScrollLink from "../components/ScrollLink";
import {
  adocSectionIdDataAttribute,
  adocSectionTitleDataAttribute,
} from "../const";
import { formatPage } from "../utils/doc";
import { AppPageContext } from "./appPage";
import {
  getAdocLink,
  getHeadingForMainSection,
  getHeadingForSecondSection,
  getHeadingForThirdSection,
  getId,
  hasPreviousListingBlock,
  isChildOfTabbedCodeBlock,
  isCopyToClipboardButton,
  isFirstLevelSubHeading,
  isListingElement,
  isMainHeading,
  isMainSection,
  isSecondLevelSubHeading,
  isSecondSection,
  isThirdSection,
  parseTabbedCodeBlockElement,
  returnAnchor,
  toText,
  getIconForTiles,
  isOpenBlockElement,
  parseTabbedBlockElement,
  hasPreviousOpenBlock,
  fetchIdIfHasLinkChild,
  isNonHeadingAnchor,
  excludeSectionbody,
  extractSectionbody,
  getClasses,
} from "./docs.utils";
import TabbedCodeBlocks from "../components/tabbedCodeBlocks";
import { useCurrentPath } from "../contexts/useCurrentPath";
import { GraphYamlData, YAMLContent } from "../../yaml-content";
import InnerApiReference from "../components/innerApiReference";
import TabbedBlocks from "../components/tabbedBlocks";
import { useLocation } from "@reach/router";
import { useSiteMetadataSiteUrl } from "../queries/useSiteMetadataSiteUrl";
import { Head as Kop } from "../components/head";

export type DocsContext = AppPageContext & {
  id: string;
  apiSchema: string | null;
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

const CopyLink = ({ id }: { id: string }) => {
  // Get the domain part of the URL (https://www.dominodatalab.com).
  const { siteUrl } = useSiteMetadataSiteUrl();
  // Get the path part of the URL (e.g. /en/latest/user_guide/fd5ba4/train-a-model-with-automl/).
  const location = useLocation();
  // Create the anchor part of the URL (e.g. #_edit_the_experiment).
  const anchor = "#" + id;
  // Put it all together.
  const url = siteUrl + location.pathname + anchor;

  return <MdLink onClick={() => copyToClipboard(url)} size={22} />;
};

export const Head: React.FC<{ data: any }> = ({ data }) => {
  return (
    <>
      <Kop title={data.asciidoc.title} />
    </>
  );
};

const Docs = (props: PageProps<DocsData, DocsContext>) => {
  const page = formatPage(props.data);
  const currentPath = useCurrentPath();

  const options: HTMLReactParserOptions = {
    replace(domNode) {
      if (!(domNode instanceof Element)) return;

      const url = getAdocLink(domNode);
      if (url) {
        return <Link to={url}>{domToReact(domNode.children, options)}</Link>;
      }

      const innerDocLinkId = fetchIdIfHasLinkChild(domNode);
      if (innerDocLinkId) {
        return (
          <ScrollLink toId={innerDocLinkId} smooth={true} hashSpy={true}>
            {domToReact(domNode.children)}
          </ScrollLink>
        );
      }

      if (isCopyToClipboardButton(domNode)) {
        return <CopyToClipboard content={toText(domNode)} />;
      }

      if (isMainSection(domNode)) {
        const heading = getHeadingForMainSection(domNode);
        if (heading) {
          const id = getId(heading);
          const mainBody = excludeSectionbody(domNode.children);
          const subSections = extractSectionbody(domNode.children);
          const nestedSections: Element[] = [];
          subSections.forEach((sub) => {
            if (sub instanceof Element) {
              sub.children.forEach((value) => {
                if (
                  value instanceof Element &&
                  (getClasses(value).includes("sect2") ||
                    getClasses(value).includes("sect3"))
                ) {
                  nestedSections.push(value);
                }
              });
            }
          });
          if (id) {
            return (
              <div className={domNode.attribs["class"]}>
                <div className="relative">
                  {returnAnchor(id)}
                  {domToReact(mainBody, options)}
                </div>
                {domToReact(subSections, options)}
              </div>
            );
          }
        }
      }

      if (isMainHeading(domNode)) {
        const id = getId(domNode);
        if (id) {
          if (currentPath) {
            return (
              <h2
                {...{
                  [adocSectionIdDataAttribute]: id,
                  [adocSectionTitleDataAttribute]: toText(domNode),
                }}
              >
                {domToReact(domNode.children, options)}
                <ScrollLink toId={id}>
                  <CopyLink id={id} />
                </ScrollLink>
              </h2>
            );
          } else {
            return (
              <h2
                {...{
                  [adocSectionIdDataAttribute]: id,
                  [adocSectionTitleDataAttribute]: toText(domNode),
                }}
              >
                <div className="flex flex-row justify-center items-center">
                  {getIconForTiles(id)}
                  {domToReact(domNode.children, options)}
                </div>
              </h2>
            );
          }
        }
      }

      if (isSecondSection(domNode)) {
        const heading = getHeadingForSecondSection(domNode);
        if (heading) {
          const id = getId(heading);
          const index = domNode.children.findIndex(
            (n) => n instanceof Element && isThirdSection(n),
          );
          const splitIndex = index === -1 ? domNode.children.length : index;
          const mainBody = domNode.children.slice(0, splitIndex);
          const subSections = domNode.children.slice(splitIndex);
          if (id) {
            return (
              <div className={domNode.attribs["class"]}>
                <div className="relative">
                  {returnAnchor(id)}
                  {domToReact(mainBody, options)}
                </div>
                {domToReact(subSections, options)}
              </div>
            );
          }
        }
      }

      if (isFirstLevelSubHeading(domNode)) {
        const id = getId(domNode);
        if (id) {
          return (
            <h3
              {...{
                [adocSectionIdDataAttribute]: id,
                [adocSectionTitleDataAttribute]: toText(domNode),
              }}
            >
              {domToReact(domNode.children, options)}
              <ScrollLink toId={id}>
                <MdLink size={22} />
              </ScrollLink>
            </h3>
          );
        }
      }

      if (isThirdSection(domNode)) {
        const heading = getHeadingForThirdSection(domNode);
        if (heading) {
          const id = getId(heading);
          if (id) {
            return (
              <div className={cn(domNode.attribs["class"], "relative")}>
                {returnAnchor(id)}
                {domToReact(domNode.children, options)}
              </div>
            );
          }
        }
      }

      if (isSecondLevelSubHeading(domNode)) {
        const id = getId(domNode);
        if (id) {
          return (
            <h4
              {...{
                [adocSectionIdDataAttribute]: id,
                [adocSectionTitleDataAttribute]: toText(domNode),
              }}
            >
              {domToReact(domNode.children, options)}
              <ScrollLink toId={id}>
                <MdLink size={22} />
              </ScrollLink>
            </h4>
          );
        }
      }

      if (isListingElement(domNode) && !hasPreviousListingBlock(domNode)) {
        const tabs = parseTabbedCodeBlockElement(domNode);
        if (tabs.length > 0) {
          return <TabbedCodeBlocks tabs={tabs} />;
        }
      }

      if (isChildOfTabbedCodeBlock(domNode)) {
        return <></>;
      }

      if (isOpenBlockElement(domNode) && !hasPreviousOpenBlock(domNode)) {
        const tabs = parseTabbedBlockElement(domNode);
        if (tabs.length > 0) {
          return <TabbedBlocks tabs={tabs} />;
        }
        return <></>;
      }

      if (isOpenBlockElement(domNode) && hasPreviousOpenBlock(domNode)) {
        return <></>;
      }

      if (isNonHeadingAnchor(domNode)) {
        const id = getId(domNode);
        if (id) {
          return (
            <div className={domNode.attribs["class"]}>
              <div className="relative">
                {returnAnchor(id)}
                {domToReact(domNode.children)}
              </div>
            </div>
          );
        }
      }

      return undefined;
    },
  };

  return (
    <article>
      <section className="min-h-screen">
        {parse(page.body, options)}
        {props.data.yaml && props.data.yaml.content && (
          <InnerApiReference
            yaml={JSON.parse(props.data.yaml.content) as YAMLContent}
          />
        )}
      </section>
    </article>
  );
};

export default Docs;

export type DocsData = {
  asciidoc: {
    title: DocNode["title"];
    fields: {
      html: DocNode["fields"]["html"];
    };
  };
  yaml: GraphYamlData | null;
};

export const pageQuery = graphql`
  query ($id: String!, $apiSchema: String) {
    asciidoc(id: { eq: $id }) {
      title
      fields {
        html
      }
    }
    yaml(path: { eq: $apiSchema }) {
      content
    }
  }
`;
